/**
 * DungeonConfig - Tunable constants for dungeon generation.
 */
const DungeonConfig = {
  // Grid dimensions
  GRID_WIDTH: 120,
  GRID_HEIGHT: 120,

  // Room constraints
  MIN_ROOMS: 12,
  MAX_ROOMS: 25,
  MIN_ROOM_SIZE: 6,
  MAX_ROOM_SIZE: 18,
  ROOM_MARGIN: 2,
  MAX_PLACEMENT_ATTEMPTS: 600,

  // Corridor
  CORRIDOR_WIDTH: 3,
  EXTRA_CONNECTIONS: 3,  // extra MST edges for loops

  // Geometry
  FLOOR_HEIGHT: 0.5,
  WALL_HEIGHT: 2,
  WALL_COLLISION_HEIGHT: 10,
  FLOOR_Y: 0,

  // Streaming
  CHUNK_SIZE: 16,
  LOAD_RADIUS: 3,
  MAX_LOADED_CUBOIDS: 600,
  STREAM_CHECK_INTERVAL: 0.5,  // seconds between chunk checks

  // Enemies
  MIN_ENEMIES_PER_ROOM: 1,
  MAX_ENEMIES_PER_ROOM: 4,
  LOOT_CHANCE: 0.3,
  ENEMY_DENSITY_MULTIPLIER: 1.3,  // per level deeper

  // Vegetation
  VEGETATION_DENSITY: 1.0,  // 0–1 density filter (1 = show all generated)

  // Multi-level (base — use getMaxLevels() for dynamic scaling)
  MAX_LEVELS: 3,

  /**
   * Dynamic dungeon depth based on player level entering the dungeon.
   * @param {number} playerLevel
   * @returns {number} number of floors (3–10)
   */
  getMaxLevels(playerLevel = 1) {
    return Math.min(10, 3 + Math.floor(playerLevel / 15));
  },

  // Per-level scaling
  getLevelConfig(levelIndex, targetLevel = 1) {
    const floorTargetLevel = targetLevel + levelIndex * 2;
    return {
      gridWidth: DungeonConfig.GRID_WIDTH + levelIndex * 15,
      gridHeight: DungeonConfig.GRID_HEIGHT + levelIndex * 15,
      roomCount: DungeonConfig.MIN_ROOMS + levelIndex * 3,
      minEnemies: DungeonConfig.MIN_ENEMIES_PER_ROOM + Math.floor(levelIndex / 2),
      maxEnemies: DungeonConfig.MAX_ENEMIES_PER_ROOM + Math.floor(levelIndex / 2),
      enemyHealthMul: 1 + levelIndex * 0.3,
      enemyAttackMul: 1 + levelIndex * 0.2,
      targetLevel: floorTargetLevel,
    };
  }
};

export default DungeonConfig;

// Tile types
export const TILE = {
  VOID: 0,
  FLOOR: 1,
  WALL: 2,
  DOOR: 3
};

// Room types
export const ROOM_TYPE = {
  START: 'start',
  NORMAL: 'normal',
  TREASURE: 'treasure',
  BOSS: 'boss',
  SHRINE: 'shrine',
  EXIT: 'exit'
};

// Enemy character types suitable for dungeon (legacy + LPC preset IDs)
// LPC preset IDs (e.g., 'skeleton-warrior') are resolved via LPCEnemyPresets.resolvePreset()
export const ENEMY_TYPES = {
  common: ['hellrat', 'skeleton-warrior', 'zombie'],
  elite: ['rogue', 'bandit-melee', 'bandit-archer', 'knight'],
  boss: ['bear', 'bandit-chief', 'skeleton-lord', 'arch-mage']
};

// Themed enemy groups — each room picks one group so enemies are consistent
// skinColors: pool of skin tones for the group (one picked per room for uniformity)
// bossSkin: optional override for boss skin (null = use room skin)
export const ENEMY_GROUPS = [
  {
    name: 'undead',
    common: ['skeleton-warrior', 'zombie'],
    elite: ['skeleton-warrior', 'skeleton-warrior'],
    boss: ['skeleton-lord'],
    skinColors: ['zombie_green', 'pale_green', 'lavender'],
    hairColors: ['ash', 'platinum', 'white']
  },
  {
    name: 'bandits',
    common: ['bandit-melee', 'bandit-melee'],
    elite: ['bandit-melee', 'bandit-archer', 'knight'],
    boss: ['bandit-chief'],
    skinColors: ['light', 'amber', 'olive', 'taupe', 'bronze'],
    hairColors: ['dark brown', 'black', 'brown', 'redhead']
  },
  {
    name: 'mages',
    common: ['bandit-melee', 'dark-mage'],
    elite: ['dark-mage', 'knight'],
    boss: ['arch-mage'],
    skinColors: ['light', 'olive', 'lavender', 'blue'],
    hairColors: ['platinum', 'white', 'ash', 'blonde']
  },
  {
    name: 'beasts',
    common: ['hellrat', 'hellrat'],
    elite: ['rogue', 'hellrat'],
    boss: ['bear'],
    skinColors: null,  // legacy characters, no LPC skin needed
    hairColors: null
  }
];

// Per-theme enemy pool selection — maps dungeon themes to weighted ENEMY_GROUPS indices
export const THEME_ENEMY_POOLS = {
  stone_crypt: {
    groups: [0, 1],   // undead, bandits
    weights: [0.7, 0.3],
  },
  cave: {
    groups: [3, 0],   // beasts, undead
    weights: [0.7, 0.3],
  },
  ruins: {
    groups: [1, 0],   // bandits, undead
    weights: [0.6, 0.4],
  },
  fire_temple: {
    groups: [2, 3],   // mages, beasts
    weights: [0.6, 0.4],
    skinOverride: ['bronze', 'sienna', 'russet'],
    hairOverride: ['red', 'redhead', 'auburn'],
  },
  ice_dungeon: {
    groups: [0, 2],   // undead, mages
    weights: [0.6, 0.4],
    skinOverride: ['pale_green', 'lavender', 'blue', 'ghost_white'],
    hairOverride: ['platinum', 'white', 'ash'],
  },
  arcane_library: {
    groups: [2, 1],   // mages, bandits
    weights: [0.6, 0.4],
  },
};
